import React, { useEffect, useRef, useState } from "react";
import { useFormModal } from "../../../components/Modals/formModal.js/FormModal";
import ReleaseList from "../Shared/ReleaseList";
import AutoCompletation from "../../../components/Common/AutoCompletation";
import { Col, ControlLabel, FormControl, FormGroup, Grid, HelpBlock, Row, Panel, PanelGroup } from 'react-bootstrap';
import { Add, Edit, Remove, Lock } from "../../../components/Icons/Icons";
import { Tooltip } from "../../../components/Utils/Tooltips";
import { useTranslation } from "react-i18next";
import { MoveItemRequest, MovementValidations } from "../../../api/Inventory/Entities/InventoryMovementEntities";
import { CustomDate } from "../../../components/CustomControls/CustomDateTime";
import InventoryMedicationService from "../../../api/Inventory/InventoryMedicationService";
import { ReleaseExpirationWarning } from "../Shared/ExpirationWarning";
import { ErrorMessage } from "../../../components/Notifications/Notifications";
import InventoryStockService from "../../../api/Inventory/InventoryStockService";
import GroupedReleaseStockList from "../Shared/GroupedReleaseStockList";
import { ExpirationGap, IsExpirationLessThan } from "../../../api/Inventory/Entities/InventoryEntities";
import { buildMedicationLabel, MISSING_COMMENT_STYLE, selectTakenKits } from "../Shared/inventoryFormRules";
import { QuantityInput } from "../Shared/QuantityInput";


export default function ProtocolItemField({ index, disabled, assignmentType, assignId, handleSubmit, movementItem, hasActions = false, actionsDisbled, handleRemove, handleReDispense = null, bsStyle, isDispensed, locked = false, itemType = null, takenKits = null }) {
    const inventoryItem = movementItem || new MoveItemRequest();
    const { t } = useTranslation();
    const expirationDateRef = useRef();
    const expirationDateInputRef = useRef();
    const quantityRef = useRef();
    const medicationRef = useRef();
    const [selectedItem, setSelectedItem] = useState(inventoryItem?.item != null ? {
        ...inventoryItem?.item,
        inventoryItemId: inventoryItem?.item?.id ?? inventoryItem?.item?.inventoryItemId,
    } : null);
    const [selectedRelease, setSelectedRelease] = useState(inventoryItem?.release);
    const [quantity, setQuantity] = useState(inventoryItem?.quantity ?? 1);
    const [ReleaseModal, ReleaseActions] = useFormModal({ FormContent: ReleaseList })
    const [editQuantity, setEditQuantity] = useState(false);
    const [availableQuantity, setAvailableQuantity] = useState(0);
    const [itemComment, setItemComment] = useState(inventoryItem.comment);
    const [selectedKit, setSelectedKit] = useState(
        inventoryItem?.assignment?.assignId != null
            ? {
                assignment: inventoryItem?.assignment,
                quantity: 0,
                releaseId: inventoryItem?.release?.id,
            }
            : null);
    const [StockModal, StockActions] = useFormModal({ FormContent: GroupedReleaseStockList })

    useEffect(() => {
        GetStock()
    }, [(selectedKit != null)])

    async function GetStock() {
        if (selectedKit != null) {
            const stockResult = await InventoryStockService.FindStockQuantity({
                intentoryItemId: selectedItem.id,
                releaseId: selectedRelease.id,
                assignmentType: assignmentType,
                assignId: assignId,
                kitNumber: selectedKit?.assignment?.kitNumber
            });
            const stockQuantity = stockResult.Value.find(s =>
                s.assignment?.assignId == selectedKit.assignment?.assignId
                && s.assignment?.assignmentType == selectedKit.assignment?.assignmentType
                && s.assignment?.kitNumber == selectedKit.assignment?.kitNumber);
            if (isDispensed) {
                setQuantity(inventoryItem.quantity * 1);
                setAvailableQuantity(stockQuantity != null ? (stockQuantity.quantity + inventoryItem.quantity) : inventoryItem.quantity);
            }
            else {
                if (quantity == null || quantity == 0)
                    setQuantity((stockQuantity?.quantity * 1) > 0 ? 1 : null);
                setAvailableQuantity(stockQuantity?.quantity ?? 0);
            }
        } else {
            setAvailableQuantity(0);
        }
    }

    const handleShowReleaseModal = async () => {
        await ReleaseActions.Show({
            title: t('inventory.commons.lotNumber'),
            onConfirmation: async (selected) => {
                setSelectedRelease(selected)
                quantityRef.current.focus()
            },
            initValue: { itemId: selectedItem.inventoryItemId, assignmentType: assignmentType, assignId: assignId, includeExpired: false }
        })
    }

    const handleShowReleaseStockModal = async () => {
        await StockActions.Show({
            title: t('inventory.commons.stock'),
            onConfirmation: (selected) => {
                setSelectedKit(selected)
            },
            initValue: {
                itemId: selectedItem.inventoryItemId,
                releaseId: selectedRelease.id,
                assignmentType: assignmentType,
                assignId: assignId,
                excludedKitNumbers: selectTakenKits(takenKits, selectedItem.inventoryItemId, selectedRelease.lotNumber)
            }
        })
    }

    const fetchItems = async (term) => {
        const result = await InventoryMedicationService.Find({ term: term, protocolId: assignId, onlyLocal: true, pageSize: 100, itemType: itemType })
        return result.Value
    }

    const curringSelected = (callback) => {
        return (selected) => {
            if (selected != null && selected.length > 0) callback(selected[0])
            else callback(null)
        }
    }

    const onCleanSelectedRelease = () => {
        setSelectedRelease(null)
        setAvailableQuantity(0)
        setQuantity(0)
    }

    const handleFormSubmit = (e) => {
        if (!isFormCompleted() && handleSubmit !== null) return
        const item = {
            item: selectedItem,
            release: selectedRelease,
            assignment: selectedKit.assignment,
            quantity: quantity,
            availableQuantity: availableQuantity,
            comment: itemComment
        }

        if (MovementValidations.ItemExpiresLessThanGap(item)) {
            ErrorMessage(t('inventory.dispense.errors.expirationLessThanTwoMonthsComment'))
            return;
        }

        handleSubmit(item)
        handleItemClean()
    }
    function isFormCompleted() {
        return selectedItem !== null
            && selectedRelease !== null
            && quantity > 0
            && !IsCommentMissing();
    }

    function IsCommentMissing() {
        return IsCommentRequire() && IsCommentEmpty();
    }

    // El comentario justifica dispensar un lote proximo a vencer: solo se exige
    // en el tramo critico. El tramo de aviso mas amplio solo se informa.
    function IsCommentRequire() {
        return selectedRelease != null
            && IsExpirationLessThan(selectedRelease, ExpirationGap.Critical)
    }
    function IsCommentEmpty() {
        return itemComment == null || itemComment.trim() === "";
    }

    function handleItemClean() {
        setSelectedItem(null)
        setSelectedRelease(null)
        setSelectedKit(null)
        setQuantity(0)
        setAvailableQuantity(0)
        setItemComment('')
        medicationRef.current.clear()
    }
    function onHandleRemove() {
        handleRemove(inventoryItem)
    }
    function handleEditQuantity() {
        setEditQuantity(true)
    }
    function onCommentChange(e) {
        setItemComment(e.target.value)
    }


    return (<Panel bsStyle={bsStyle} key={`multi-item-${index}`} eventKey={`multi-item-${index}`}>
        <Panel.Heading>
            <Panel.Title >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Panel.Title toggle>
                        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                            {selectedItem != null ?
                                (<>
                                    {selectedItem.presentation + (quantity > 0 ? ` - Total: ${quantity}` : "")}
                                    {locked && <>
                                        <Lock className="protocol-dispense__lock-icon" aria-label={t('inventory.commons.reDispensedLocked')} />
                                        <span className="protocol-dispense__redispensed-tag">{t('inventory.commons.reDispense')}</span>
                                    </>}
                                    {selectedRelease != null && <span style={{ marginRight: '1rem' }}>
                                        <ReleaseExpirationWarning release={selectedRelease} />
                                    </span>}
                                </>)
                                : "Nueva Medicacion"}
                        </div>
                    </Panel.Title>
                    {hasActions && <div>
                        {handleReDispense != null && (
                            <button className="btn btn-info" type="button" disabled={actionsDisbled}
                                onClick={() => handleReDispense(inventoryItem)} style={{ marginRight: '.5rem' }}>
                                <Tooltip tooltip={t('inventory.commons.reDispense')} id={`tooltip-redispense-${index}`}>
                                    <span aria-hidden="true"><Add /></span>
                                </Tooltip>
                            </button>
                        )}
                        <button className="btn btn-danger" type="button" disabled={actionsDisbled} onClick={onHandleRemove} style={{ marginRight: '.5rem' }}>
                            <Tooltip tooltip={t('commons.remove')} id={"tooltipedit-eliminarMedicamento"}>
                                <span aria-hidden="true"><Remove /></span>
                            </Tooltip>
                        </button>
                        {/* <button className="btn btn-info" type="button" disabled={actionsDisbled} onClick={handleEditQuantity}>
                            <Tooltip tooltip={t('buttons.edit')} id={"tooltipedit-editarMedicamento"}>
                                <span aria-hidden="true"><Edit /></span>
                            </Tooltip>
                        </button> */}
                    </div>}
                </div>
            </Panel.Title>
        </Panel.Heading>
        <Panel.Body collapsible>
            <div>
                <div className='input-group-container'>
                    <FormGroup style={{ width: "100%" }}>
                        <AutoCompletation
                            id={'medication'}
                            ref={medicationRef}
                            labelKey={(option) => buildMedicationLabel(option)}
                            placeholder={t('inventory.dispense.findMedication')}
                            fetchOptions={fetchItems}
                            onChange={curringSelected(setSelectedItem)}
                            disabled={disabled || selectedRelease != null}
                            defaultSelectedOptions={selectedItem != null ? [selectedItem] : []}
                        />
                    </FormGroup>
                </div>
                <hr />
                {selectedRelease == null && <div className='input-group-container'>
                    <FormGroup style={{ width: "100%" }}>
                        <button className="btn btn-primary" type="button" disabled={disabled || selectedItem == null} onClick={_ => handleShowReleaseModal()}>
                            <Tooltip>
                                <Add /> {t('inventory.dispense.selectLotNumber')}
                            </Tooltip>
                        </button>
                    </FormGroup>
                </div>
                    || <div className='input-group-container'>
                        <FormGroup style={{ width: "100%" }}>
                            <input disabled={true} value={selectedRelease?.lotNumber} className="rbt-input-main form-control rbt-input" />
                        </FormGroup>

                        <FormGroup style={{ width: "100%" }} >
                            <CustomDate
                                dateRef={expirationDateRef}
                                inputRef={expirationDateInputRef}
                                name={`expirationDate`}
                                onChange={() => { }}
                                onBlur={() => { }}
                                value={selectedRelease?.expirationDate ?? null}
                                defaulValue={null}
                                dateFormat="DD/MM/YYYY"
                                disabled={true}
                                inputProps={{ placeholder: t('commons.expirationDate') }}
                            />
                        </FormGroup>

                        {!disabled && <FormGroup style={{ width: "100%" }}>
                            <button className="btn btn-danger" type="button" id="clearRelease" disabled={disabled || selectedRelease == null} onClick={onCleanSelectedRelease}>
                                <Tooltip tooltip={t('commons.clear')} id={"tooltipedit-limpiarpaciente"}>
                                    <span aria-hidden="true"><Remove /></span>
                                </Tooltip>
                            </button>
                        </FormGroup>}
                    </div>}
                <hr />

                {selectedKit == null && <div className='input-group-container'>
                    <FormGroup style={{ width: "25%" }}>
                        <button type="button" className="btn btn-primary" disabled={selectedRelease == null} onClick={_ => handleShowReleaseStockModal()}>
                            <Tooltip tooltip={'seleccionar stock'} style={{ gap: '1rem' }}>
                                <Add />
                                {t('inventory.dispense.selectKit')}
                            </Tooltip>
                        </button>
                    </FormGroup>
                    <QuantityInput ref={quantityRef}
                        value={quantity}
                        onChange={setQuantity}
                        max={availableQuantity}
                        disabled={locked || (!editQuantity && (disabled || selectedItem == null || selectedRelease == null))}
                        placeholder={t('inventory.dispense.quantity')}
                        style={{ width: "50%" }} />
                </div>
                    || <div className='input-group-container'>
                        <FormGroup style={{ width: "25%" }}>
                            <input disabled={true} value={selectedKit?.assignment?.kitNumber
                                ?? ' - '} className="rbt-input-main form-control rbt-input" />
                        </FormGroup>

                        {!disabled && <FormGroup>
                            <button type="button" className="btn btn-danger" id="clearkit" disabled={selectedRelease == null} onClick={() => setSelectedKit(null)}>
                                <Tooltip tooltip={t('commons.clear')} id={"tooltipedit-limpiarpaciente"}>
                                    <span aria-hidden="true"><Remove /></span>
                                </Tooltip>
                            </button>
                        </FormGroup>}
                        <QuantityInput ref={quantityRef}
                            value={quantity}
                            onChange={setQuantity}
                            max={availableQuantity}
                            disabled={locked || (!editQuantity && (disabled || selectedItem == null || selectedRelease == null))}
                            placeholder={t('inventory.dispense.quantity')}
                            style={{ width: "50%" }} />
                    </div>}
                <hr />
                {IsCommentRequire()
                    && <div className='input-group-container'>
                        <FormGroup style={{ width: "100%" }} validationState={IsCommentMissing() ? "error" : null}>
                            <ControlLabel>{t("commons.comment")}</ControlLabel>
                            <textarea className="form-control" rows={3} value={itemComment} name="item.comentary"
                                style={IsCommentMissing() ? MISSING_COMMENT_STYLE : undefined}
                                disabled={!editQuantity && disabled} onChange={onCommentChange} />
                            {IsCommentMissing() && <HelpBlock>{t('inventory.dispense.commentRequiredForExpiringLot')}</HelpBlock>}
                        </FormGroup>
                    </div>}
                <div style={{ display: "flex", flexDirection: 'column', justifyContent: 'end' }} >
                    <div style={{ alignSelf: 'self-end', marginTop: '1rem' }}>
                        <button type="button" className='btn btn-success' disabled={!editQuantity && (disabled || !isFormCompleted())} onClick={_ => handleFormSubmit()}>{t('buttons.add')}</button>
                    </div>
                </div>
                <ReleaseModal />
                <StockModal />
            </div>
        </Panel.Body>
    </Panel>)
}